import React, { useState } from 'react';
import { Modal, Button, Carousel } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css'; 
import moment from 'moment';
import styled, { keyframes } from 'styled-components';

AOS.init({
    duration : 2000
});

// Room colors exactly matching your Features component
const getRoomColors = (roomname) => {
    let color = '';
    
    if (roomname === "Love Suite ") {
        color = 'red';
    }
    if (roomname === "Joy Suite") {
        color = '#D56106';
    }
    if (roomname === "Hope Suite") {
        color = '#2206D5';
    }
    if (roomname === "Peace Suite") {
        color = '#95D8CE';
    }
    if (roomname === "Grace Suite") {
        color = '#CEA0E5';
    }
    if (roomname === "Faith Suite") {
        color = '#F3C731';
    }
    
    return color || '#3498db';
};

const shimmer = keyframes`
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
`;

const RoomContainer = styled.div`
    max-width: 1200px;
    height: 100%;
    margin: 2rem auto;
    padding: 0 1rem;
    
    @media (max-width: 1024px) {
        max-width: 900px;
    }
    
    @media (max-width: 768px) {
        max-width: 700px;
    }
`;

const RoomCard = styled.div`
    background: white;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    transition: all 0.3s ease;
    border: 3px solid ${props => props.roomColor};
    
    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 40px rgba(0, 0, 0, 0.18);
    }
`;

const ImageCarouselSection = styled.div`
    position: relative;
    height: 650px;
    overflow: hidden;
    
    @media (max-width: 1400px) {
        height: 600px;
    }
    
    @media (max-width: 1024px) {
        height: 500px;
    }
    
    @media (max-width: 768px) {
        height: 400px;
    }
    
    @media (max-width: 480px) {
        height: 320px;
    }
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 5;
        pointer-events: none;
    }
    
    &:hover::after {
        opacity: 1;
        animation: ${shimmer} 1.2s ease-out;
    }
`;

const StyledCarousel = styled(Carousel)`
    height: 100%;
    
    .carousel-inner,
    .carousel-item {
        height: 100%;
    }
    
    .carousel-item img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        min-height: 100%;
        max-height: 100%;
    }
    
    .carousel-control-prev,
    .carousel-control-next {
        background: ${props => props.roomColor}dd;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        top: 50%;
        transform: translateY(-50%);
        opacity: 0.8;
        border: 2px solid white;
        transition: all 0.3s ease;
        
        &:hover {
            opacity: 1;
            background: ${props => props.roomColor};
            transform: translateY(-50%) scale(1.1);
        }
    }
    
    .carousel-control-prev {
        left: 15px;
    }
    
    .carousel-control-next {
        right: 15px;
    }
    
    .carousel-indicators {
        bottom: 15px;
        margin: 0;
        
        button {
            background: ${props => props.roomColor};
            border-radius: 50%;
            width: 8px;
            height: 8px;
            border: 1px solid white;
            margin: 0 3px;
            opacity: 0.7;
            transition: all 0.3s ease;
            
            &.active {
                opacity: 1;
                background: white;
                border: 2px solid ${props => props.roomColor};
                transform: scale(1.2);
            }
            
            &:hover {
                transform: scale(1.1);
                opacity: 0.9;
            }
        }
    }
`;

const RoomBadge = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    background: ${props => props.roomColor};
    color: white;
    padding: 6px 12px;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const ContentSection = styled.div`
    padding: 1rem;
`;

const RoomHeader = styled.div`
    text-align: center;
    margin-bottom: 0.8rem;
`;

const RoomTitle = styled.h2`
    color: ${props => props.roomColor};
    font-size: 1.8rem;
    font-weight: 800;
    margin: 0 0 0.2rem 0;
    letter-spacing: 0.5px;
`;

const RoomSubtitle = styled.p`
    color: #666;
    font-size: 0.85rem;
    margin: 0;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

const DescriptionArea = styled.div`
    background: ${props => props.roomColor}10;
    border-radius: 10px;
    padding: 0.8rem;
    border-left: 3px solid ${props => props.roomColor};
    text-align: center;
    margin-bottom: 1rem;
`;

const DescriptionText = styled.p`
    color: #555;
    font-size: 1.1rem;
    line-height: 1.4;
    margin: 0;
    
    @media (min-width: 1025px) {
        font-size: 1.2rem;
        line-height: 1.5;
    }
    
    @media (max-width: 768px) {
        font-size: 1rem;
        line-height: 1.4;
    }
    
    @media (max-width: 480px) {
        font-size: 0.9rem;
        line-height: 1.3;
    }
`;

const ViewMoreBtn = styled.button`
    background: none;
    border: none;
    color: ${props => props.roomColor};
    font-weight: 600;
    cursor: pointer;
    margin-top: 0.5rem;
    font-size: 0.8rem;
    text-decoration: underline;
    transition: all 0.2s ease;
    
    &:hover {
        opacity: 0.8;
    }
`;

const PriceAndActionsSection = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1rem;
    align-items: center;
    
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: 0.8rem;
        text-align: center;
    }
`;

const PriceDisplay = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
    
    @media (max-width: 600px) {
        justify-content: center;
    }
`;

const PriceItem = styled.div`
    background: ${props => props.roomColor}15;
    border: 2px solid ${props => props.roomColor}60;
    border-radius: 8px;
    padding: 0.4rem 0.6rem;
    text-align: center;
    min-width: 70px;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${props => props.roomColor}25;
        transform: translateY(-1px);
    }
`;

const PriceValue = styled.div`
    font-size: 1rem;
    font-weight: 800;
    color: ${props => props.roomColor};
    line-height: 1;
`;

const PriceType = styled.div`
    font-size: 0.65rem;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
    margin-top: 1px;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 0.6rem;
    
    @media (max-width: 600px) {
        justify-content: center;
    }
`;

const ActionBtn = styled.button`
    background: ${props => props.primary ? props.roomColor : 'white'};
    border: 2px solid ${props => props.roomColor};
    color: ${props => props.primary ? 'white' : props.roomColor};
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 90px;
    
    &:hover {
        background: ${props => props.roomColor};
        color: white;
        transform: translateY(-1px);
        box-shadow: 0 4px 15px ${props => props.roomColor}40;
    }
`;

const GalleryModal = styled(Modal)`
    .modal-content {
        border-radius: 15px;
        border: 3px solid ${props => props.roomColor};
        overflow: hidden;
    }
    
    .modal-header {
        background: ${props => props.roomColor};
        color: white;
        border: none;
        padding: 1.2rem;
        position: relative;
    }
    
    .modal-title {
        color: white;
        font-weight: 700;
        font-size: 1.3rem;
        margin: 0;
        padding-right: 2rem;
    }
    
    .btn-close {
        filter: invert(1);
        opacity: 0.8;
        position: absolute;
        top: 50%;
        right: 1rem;
        transform: translateY(-50%);
        margin: 0;
        
        &:hover {
            opacity: 1;
        }
    }
`;

const ModalCarousel = styled.div`
    .carousel-item img {
        height: 450px;
        object-fit: cover;
        border-radius: 8px;
        
        @media (max-width: 768px) {
            height: 380px;
        }
    }
    
    .carousel-control-prev,
    .carousel-control-next {
        background: ${props => props.roomColor};
        border-radius: 50%;
        width: 40px;
        height: 40px;
        top: 50%;
        transform: translateY(-50%);
        opacity: 0.8;
        
        &:hover {
            opacity: 1;
        }
    }
    
    .carousel-indicators {
        bottom: 15px;
        margin-bottom: 0;
        
        button {
            background: ${props => props.roomColor};
            border-radius: 50%;
            width: 10px;
            height: 10px;
            border: none;
            margin: 0 4px;
            padding: 0;
            opacity: 0.7;
            
            &.active {
                opacity: 1;
                background: white;
                border: 2px solid ${props => props.roomColor};
                width: 12px;
                height: 12px;
            }
        }
    }
`;

function Room({ room, fromdate, todate }) {
    const [showModal, setShowModal] = useState(false);
    const [expandedText, setExpandedText] = useState(false);
    
    const roomColor = getRoomColors(room.name);
    
    const openModal = () => setShowModal(true);
    const closeModal = () => setShowModal(false);
    
    const truncateText = (text, maxLength = 120) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    return (
        <RoomContainer data-aos='fade-up'>
            <RoomCard roomColor={roomColor}>
                <ImageCarouselSection>
                    <StyledCarousel 
                        roomColor={roomColor} 
                        indicators={true} 
                        controls={true} 
                        interval={4000}
                        pause="hover"
                        wrap={true}
                    >
                        {room.imageurls.slice(0, 3).map((url, index) => (
                            <Carousel.Item key={index}>
                                <img 
                                    src={url}
                                    alt={`${room.name} - View ${index + 1}`}
                                />
                            </Carousel.Item>
                        ))}
                    </StyledCarousel>
                    <RoomBadge roomColor={roomColor}>
                        {room.type}
                    </RoomBadge>
                </ImageCarouselSection>
                
                <ContentSection>
                    <RoomHeader>
                        <RoomTitle roomColor={roomColor}>
                            {room.name}
                        </RoomTitle>
                        <RoomSubtitle>
                            Luxury Suite
                        </RoomSubtitle>
                    </RoomHeader>
                    
                    <DescriptionArea roomColor={roomColor}>
                        <DescriptionText>
                            {expandedText ? room.descriptiontwo : truncateText(room.descriptiontwo)}
                        </DescriptionText>
                        {room.descriptiontwo && room.descriptiontwo.length > 120 && (
                            <ViewMoreBtn 
                                roomColor={roomColor}
                                onClick={() => setExpandedText(!expandedText)}
                            >
                                {expandedText ? 'Show Less' : 'View More'}
                            </ViewMoreBtn>
                        )}
                    </DescriptionArea>
                    
                    <PriceAndActionsSection>
                        <PriceDisplay>
                            {room.rentPerDay && (
                                <PriceItem roomColor={roomColor}>
                                    <PriceValue roomColor={roomColor}>${room.rentPerDay}</PriceValue>
                                    <PriceType>Night</PriceType>
                                </PriceItem>
                            )}
                            {room.rentPerDay2 && (
                                <PriceItem roomColor={roomColor}>
                                    <PriceValue roomColor={roomColor}>${room.rentPerDay2}</PriceValue>
                                    <PriceType>{room.rentPerDay2Title ? room.rentPerDay2Title.substring(0, 6) : "Weekend"}</PriceType>
                                </PriceItem>
                            )}
                            {room.rentPerDay3 && (
                                <PriceItem roomColor={roomColor}>
                                    <PriceValue roomColor={roomColor}>${room.rentPerDay3}</PriceValue>
                                    <PriceType>Special</PriceType>
                                </PriceItem>
                            )}
                        </PriceDisplay>
                        
                        <ActionButtons>
                            <ActionBtn 
                                roomColor={roomColor}
                                primary
                                as="a"
                                href="https://secure.thinkreservations.com/littleheavenbedandbreakfast/reservations"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Book
                            </ActionBtn>
                            <ActionBtn roomColor={roomColor} onClick={openModal}>
                                Gallery
                            </ActionBtn>
                        </ActionButtons>
                    </PriceAndActionsSection>
                </ContentSection>
            </RoomCard>

            <GalleryModal roomColor={roomColor} show={showModal} onHide={closeModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{room.name} Gallery</Modal.Title>        
                </Modal.Header>
                <Modal.Body style={{padding: '1.5rem'}}>
                    <ModalCarousel roomColor={roomColor}>
                        <Carousel>
                            {room.imageurls.map((url, index) => (
                                <Carousel.Item key={index}>
                                    <img 
                                        className="d-block w-100"
                                        src={url}
                                        alt={`${room.name} - Image ${index + 1}`}
                                    />
                                </Carousel.Item>
                            ))}
                        </Carousel>
                    </ModalCarousel>
                    
                    {room.description && (
                        <div style={{
                            marginTop: '1.5rem',
                            padding: '1rem',
                            background: `${roomColor}08`,
                            borderRadius: '10px',
                            border: `2px solid ${roomColor}30`
                        }}>
                            <h4 style={{
                                color: roomColor,
                                fontWeight: '700',
                                marginBottom: '0.8rem',
                                fontSize: '1.1rem'
                            }}>
                                Suite Details
                            </h4>
                            <p style={{
                                fontSize: '0.95rem',
                                lineHeight: '1.5',
                                color: '#555',
                                margin: 0
                            }}>
                                {room.description}
                            </p>
                        </div>
                    )}
                </Modal.Body>
            </GalleryModal>
        </RoomContainer>
    );
}

export default Room;